import { useEffect, useState } from "react";

import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spinner,
  Stack,
  Switch,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  useColorModeValue,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaStar } from "react-icons/fa";

import { fetchGroupsForUser } from "~/api/groups";
import { useAppState } from "~/hooks/useAppState";
import { deleteGroup } from "~/repositories/groupRepository";
import { setDefaultGroup as setDefaultGroupApi } from "~/repositories/groups.repo";
import { useAppStore } from "~/store";
import type { Group } from "~/types/group";

// Interface for group with role property
interface GroupWithRole extends Group {
  role: string;
  isDefault?: boolean;
}

// Separate GroupsTable component
const GroupsTable = ({
  groups,
  defaultGroupId,
  onDeleteGroup
}: {
  groups: GroupWithRole[];
  defaultGroupId?: number;
  onDeleteGroup: (group: GroupWithRole) => void;
}) => {
  const toast = useToast();

  const handleTransferOwnership = () => {
    toast({
      title: "Not implemented",
      description: "Transfer ownership is not available yet",
      status: "info",
      duration: 3000,
      isClosable: true
    });
  };

  // Order groups by default status first, then by name
  const orderedGroups = [...groups].sort((a, b) => {
    // Default group goes first
    if (a.id === defaultGroupId && b.id !== defaultGroupId) return -1;
    if (a.id !== defaultGroupId && b.id === defaultGroupId) return 1;
    // Then sort by name
    return a.name.localeCompare(b.name);
  });

  return (
    <Box overflowX="auto" maxW={{ base: "100%", md: "80%" }} mx="auto">
      <Table variant="simple" mb={6} size="sm" className="group-table">
        <Thead>
          <Tr>
            <Th width="5%"></Th> {/* Default indicator column */}
            <Th width="25%">Name</Th>
            <Th width="45%">Description</Th>
            <Th width="15%">Role</Th>
            <Th width="5%"></Th> {/* Actions column */}
          </Tr>
        </Thead>
        <Tbody>
          {orderedGroups.map((group) => (
            <Tr key={group.id} bg={group.id === defaultGroupId ? "gray.50" : undefined}>
              <Td width="5%" textAlign="center">
                {group.id === defaultGroupId && (
                  <Tooltip label="This is your default group" placement="top">
                    <Box>
                      <FaStar color="#F6AD55" size="14px" />
                    </Box>
                  </Tooltip>
                )}
              </Td>
              <Td fontWeight={group.id === defaultGroupId ? "bold" : "medium"} fontSize="sm" py={2}>
                {group.name}
              </Td>
              <Td fontSize="sm" py={2}>
                {group.description}
              </Td>
              <Td py={2}>
                <Badge colorScheme={group.role === "Owner" ? "green" : "blue"} fontSize="xs">
                  {group.role}
                </Badge>
              </Td>
              <Td textAlign="right" py={2}>
                <Menu>
                  <MenuButton
                    as={IconButton}
                    aria-label="Group options"
                    icon={<BsThreeDotsVertical />}
                    variant="ghost"
                    size="xs"
                  />
                  <MenuList>
                    <MenuItem color="red.500" onClick={() => onDeleteGroup(group)} isDisabled={group.role !== "Owner"}>
                      Delete Group
                    </MenuItem>
                    <MenuItem onClick={handleTransferOwnership} isDisabled={true}>
                      Transfer Ownership
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

// Group Settings component replacing Settings1
const GroupSettings = () => {
  const [ownedGroups, setOwnedGroups] = useState<Group[]>([]);
  const [memberGroups, setMemberGroups] = useState<Group[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<GroupWithRole | null>(null);
  const [confirmGroupName, setConfirmGroupName] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { userHomeData } = useAppState("userHomeData");
  const setUserHomeData = useAppStore((state) => state.setUserHomeData);

  // Fetch the user's groups when the component mounts
  const fetchGroups = async () => {
    setIsLoading(true);
    try {
      const response = await fetchGroupsForUser();
      if (response.success) {
        setOwnedGroups(response.ownedGroups || []);
        setMemberGroups(response.memberOfGroups || []);
      } else {
        setError(response.error || "Failed to fetch groups");
        toast({
          title: "Error fetching groups",
          description: response.error || "Something went wrong",
          status: "error",
          duration: 5000,
          isClosable: true
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [toast]);

  // Open confirmation modal for group deletion
  const handleDeleteGroup = (group: GroupWithRole) => {
    setGroupToDelete(group);
    setConfirmGroupName("");
    onOpen();
  };

  // Handle setting a default group
  const handleSetDefaultGroup = async (groupIdString: string) => {
    if (!groupIdString) return;

    setIsSettingDefault(true);
    try {
      const groupId = parseInt(groupIdString, 10);
      const result = await setDefaultGroupApi(groupId);

      if (result.success && result.defaultGroup) {
        // Update userHomeData with the new default group
        if (userHomeData) {
          setUserHomeData({
            ...userHomeData,
            defaultGroup: result.defaultGroup
          });
        }

        toast({
          title: "Default group updated",
          description: `"${result.defaultGroup.name}" is now your default group`,
          status: "success",
          duration: 3000,
          isClosable: true
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to update default group",
          status: "error",
          duration: 5000,
          isClosable: true
        });
      }
    } catch (error) {
      console.error("Error setting default group:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        status: "error",
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsSettingDefault(false);
    }
  };

  // Confirm and execute deletion
  const confirmDelete = async () => {
    if (!groupToDelete) return;

    // Check if the typed name matches the group name
    if (confirmGroupName !== groupToDelete.name) {
      toast({
        title: "Group name doesn't match",
        description: "Please type the exact group name to confirm deletion",
        status: "warning",
        duration: 3000,
        isClosable: true
      });
      return;
    }

    console.log("Confirming deletion for group:", groupToDelete);
    setIsDeleting(true);

    try {
      // First try a simple GET to the group to check authentication
      console.log("Testing authentication with GET request");
      try {
        const testResponse = await fetch(`/api/groups/${groupToDelete.id}`, {
          method: "GET",
          credentials: "include"
        });
        console.log("Auth test response:", testResponse.status, testResponse.statusText);

        if (!testResponse.ok) {
          console.error("Authentication test failed");
          toast({
            title: "Authentication Error",
            description: "Could not verify access to the group. Please try logging out and back in.",
            status: "error",
            duration: 5000,
            isClosable: true
          });
          setIsDeleting(false);
          return;
        }
      } catch (authError) {
        console.error("Auth test error:", authError);
      }

      console.log("Calling deleteGroup with ID:", groupToDelete.id, "Type:", typeof groupToDelete.id);
      const result = await deleteGroup(groupToDelete.id);
      console.log("Delete result:", result);

      if (result.success) {
        toast({
          title: "Group deleted",
          description: result.message || "The group has been successfully deleted",
          status: "success",
          duration: 3000,
          isClosable: true
        });

        // Refresh the groups list
        await fetchGroups();
      } else {
        toast({
          title: "Error deleting group",
          description: result.message || "Failed to delete the group",
          status: "error",
          duration: 5000,
          isClosable: true
        });
      }
    } catch (error) {
      console.error("Error in confirmDelete:", error);
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsDeleting(false);
      onClose();
      setGroupToDelete(null);
      setConfirmGroupName("");
    }
  };

  // Combine all groups for display in the table
  const allGroups: GroupWithRole[] = [
    ...ownedGroups.map((group) => ({ ...group, role: "Owner" })),
    ...memberGroups.map((group) => ({ ...group, role: "Member" }))
  ];

  return (
    <Box>
      <Heading as="h2" size="md" mb={4}>
        Manage Groups
      </Heading>
      <Text mb={4}>Configure group settings and manage your groups.</Text>

      {isLoading ? (
        <Flex justifyContent="center" my={8}>
          <Spinner size="xl" color="brand.primary" />
        </Flex>
      ) : error ? (
        <Box p={4} bg="red.50" color="red.500" borderRadius="md" mb={4}>
          {error}
        </Box>
      ) : allGroups.length === 0 ? (
        <Box p={4} bg="gray.50" borderRadius="md" textAlign="center">
          <Text mb={2}>You don't have any groups yet.</Text>
          <Button colorScheme="brand" size="sm" mt={2}>
            Create Your First Group
          </Button>
        </Box>
      ) : (
        <>
          {/* Group Table Component */}
          <GroupsTable
            groups={allGroups}
            defaultGroupId={userHomeData?.defaultGroup?.id}
            onDeleteGroup={handleDeleteGroup}
          />

          <Flex maxW={{ base: "100%", md: "80%" }} mx="auto" direction="column" mt={6}>
            <Button colorScheme="brand" mt={2} alignSelf="flex-start">
              Create New Group
            </Button>
          </Flex>

          {/* Delete Confirmation Modal */}
          <Modal
            isOpen={isOpen}
            onClose={onClose}
            isCentered
            closeOnOverlayClick={!isDeleting}
            closeOnEsc={!isDeleting}
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader color="red.500">Delete Group</ModalHeader>
              <ModalCloseButton isDisabled={isDeleting} />
              <ModalBody>
                <Text fontWeight="bold" mb={4}>
                  This action cannot be undone.
                </Text>
                <Text mb={4}>
                  Deleting this group will remove all members and their associated data. This action is permanent and
                  cannot be reversed.
                </Text>
                <FormControl>
                  <FormLabel>
                    To confirm, type the group name <b>{groupToDelete?.name}</b> below:
                  </FormLabel>
                  <Input
                    value={confirmGroupName}
                    onChange={(e) => setConfirmGroupName(e.target.value)}
                    placeholder={groupToDelete?.name || ""}
                    autoFocus
                    isDisabled={isDeleting}
                  />
                </FormControl>
              </ModalBody>
              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isDeleting}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={confirmDelete}
                  isDisabled={confirmGroupName !== groupToDelete?.name || isDeleting}
                  isLoading={isDeleting}
                  loadingText="Deleting"
                >
                  Delete Group
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </>
      )}
    </Box>
  );
};

// These components remain unchanged
const Settings2 = () => {
  return (
    <Box>
      <Heading as="h2" size="md" mb={4}>
        App Settings
      </Heading>
      <Text mb={4}>Configure application behavior and preferences.</Text>

      <Stack spacing={6} mt={6}>
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="analytics-switch" mb="0">
            Enable Analytics
          </FormLabel>
          <Switch id="analytics-switch" defaultChecked />
        </FormControl>

        <FormControl>
          <FormLabel>Default View</FormLabel>
          <Select defaultValue="members">
            <option value="members">Members</option>
            <option value="dashboard">Dashboard</option>
            <option value="analyze">Analyze</option>
          </Select>
        </FormControl>

        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="notifications-switch" mb="0">
            In-App Notifications
          </FormLabel>
          <Switch id="notifications-switch" defaultChecked />
        </FormControl>
      </Stack>
    </Box>
  );
};

const Settings3 = () => {
  const [dataRetention, setDataRetention] = useState("30");

  return (
    <Box>
      <Heading as="h2" size="md" mb={4}>
        Privacy Settings
      </Heading>
      <Text mb={4}>Manage your privacy and data preferences.</Text>

      <Stack spacing={6} mt={6}>
        <FormControl>
          <FormLabel>Data Retention Period (days)</FormLabel>
          <Slider defaultValue={30} min={7} max={90} step={1} colorScheme="brand">
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb boxSize={6} />
          </Slider>
        </FormControl>

        <FormControl>
          <FormLabel>Data Sharing</FormLabel>
          <RadioGroup onChange={setDataRetention} value={dataRetention}>
            <Stack>
              <Radio value="none">None - Keep data private</Radio>
              <Radio value="anonymous">Anonymous - Share anonymized data</Radio>
              <Radio value="selective">Selective - Share with selected members</Radio>
            </Stack>
          </RadioGroup>
        </FormControl>

        <FormControl>
          <FormLabel>Export Format</FormLabel>
          <HStack spacing={4}>
            <Radio defaultChecked>CSV</Radio>
            <Radio>PDF</Radio>
            <Radio>JSON</Radio>
          </HStack>
        </FormControl>
      </Stack>
    </Box>
  );
};

const SettingsLayout = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Use breakpoint value to determine orientation
  const tabsOrientation = useBreakpointValue({ base: "horizontal", md: "vertical" }) as "horizontal" | "vertical";
  const isFitted = useBreakpointValue({ base: true, md: false });

  const handleTabsChange = (index: number) => {
    setTabIndex(index);
  };

  return (
    <Box p={4} maxW="1200px" mx="auto">
      <Heading as="h1" size="xl" mb={6}>
        Settings
      </Heading>

      <Flex
        direction={{ base: "column", md: "row" }}
        bg={bgColor}
        borderRadius="md"
        overflow="hidden"
        boxShadow="md"
        border="1px solid"
        borderColor={borderColor}
      >
        <Tabs
          orientation={tabsOrientation}
          variant="line"
          onChange={handleTabsChange}
          index={tabIndex}
          flex={{ md: 1 }}
          colorScheme="brand"
          display="flex"
          flexDirection={{ base: "column", md: "row" }}
          height={{ md: "500px" }}
          isFitted={isFitted}
        >
          <TabList
            borderRight={{ md: "1px solid" }}
            borderColor={{ md: borderColor }}
            borderBottom={{ base: "1px solid", md: "none" }}
            borderBottomColor={{ base: borderColor, md: "none" }}
            minW={{ md: "200px" }}
            bg={useColorModeValue("gray.50", "gray.900")}
            flexDirection={{ base: "row", md: "column" }}
          >
            <Tab justifyContent={{ md: "flex-start" }} fontWeight="medium">
              Group Settings
            </Tab>
            <Tab justifyContent={{ md: "flex-start" }} fontWeight="medium">
              App Settings
            </Tab>
            <Tab justifyContent={{ md: "flex-start" }} fontWeight="medium">
              Privacy Settings
            </Tab>
          </TabList>

          <TabPanels flex={{ md: 3 }}>
            <TabPanel>
              <GroupSettings />
            </TabPanel>
            <TabPanel>
              <Settings2 />
            </TabPanel>
            <TabPanel>
              <Settings3 />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Flex>
    </Box>
  );
};

export default SettingsLayout;
